export const timeFormatOptions: Intl.DateTimeFormatOptions = { timeStyle: 'short' };
export const shortDateFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'short' };
export const shortMonthFormatOptions: Intl.DateTimeFormatOptions = { month: 'short' };
export const shortWeekdayFormatOptions: Intl.DateTimeFormatOptions = { weekday: 'short' };
export const dayFormatOptions: Intl.DateTimeFormatOptions = { day: 'numeric' };
export const shortDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    timeStyle: 'short',
    dateStyle: 'short',
};
export const longWeekdayDateFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
};

type Formatter = {
    format(
        date: Date,
        options: { locale: string; timeZone?: Intl.DateTimeFormatOptions['timeZone'] }
    ): string;
    formatRange(
        startDate: Date,
        endDate: Date,
        options: { locale: string; timeZone?: Intl.DateTimeFormatOptions['timeZone'] }
    ): string;
    formatToParts(
        date: Date,
        options: { locale: string; timeZone?: Intl.DateTimeFormatOptions['timeZone'] }
    ): Intl.DateTimeFormatPart[];
    formatRangeToParts(
        startDate: Date,
        endDate: Date,
        options: { locale: string; timeZone?: Intl.DateTimeFormatOptions['timeZone'] }
    ): Intl.DateTimeRangeFormatPart[];
    resolvedOptions(options: {
        locale: string;
        timeZone?: Intl.DateTimeFormatOptions['timeZone'];
    }): Intl.ResolvedDateTimeFormatOptions;
};

const formatterFactory = (options: Intl.DateTimeFormatOptions): Formatter => {
    return {
        format: (date, { locale, timeZone }) =>
            new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date),
        formatRange: (startDate, endDate, { locale, timeZone }) =>
            new Intl.DateTimeFormat(locale, { ...options, timeZone }).formatRange(
                startDate,
                endDate
            ),
        formatToParts: (date, { locale, timeZone }) =>
            new Intl.DateTimeFormat(locale, { ...options, timeZone }).formatToParts(date),
        formatRangeToParts: (startDate, endDate, { locale, timeZone }) =>
            new Intl.DateTimeFormat(locale, { ...options, timeZone }).formatRangeToParts(
                startDate,
                endDate
            ),
        resolvedOptions: ({ locale, timeZone }) =>
            new Intl.DateTimeFormat(locale, { ...options, timeZone }).resolvedOptions(),
    };
};

export const timeFormat = formatterFactory(timeFormatOptions);
export const shortDateFormat = formatterFactory(shortDateFormatOptions);
export const shortMonthFormat = formatterFactory(shortMonthFormatOptions);
export const shortWeekdayFormat = formatterFactory(shortWeekdayFormatOptions);
export const dayFormat = formatterFactory(dayFormatOptions);
export const shortDateTimeFormat = formatterFactory(shortDateTimeFormatOptions);
export const longWeekdayDateFormat = formatterFactory(longWeekdayDateFormatOptions);

export const getTimeZoneName = (
    date: Date,
    timeZone: NonNullable<Intl.DateTimeFormatOptions['timeZone']>,
    locale: string,
    format: Intl.DateTimeFormatOptions['timeZoneName'] = 'short'
) => {
    const offsetPart = new Intl.DateTimeFormat(locale, { timeZoneName: format, timeZone })
        .formatToParts(date)
        .find((part) => {
            return part.type === 'timeZoneName';
        });
    if (!offsetPart) {
        return '';
    }
    return offsetPart.value;
};
